import React, { useState, useEffect } from 'react';
import { useUser } from './usersContext';
import api from './utils/api';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { Link, useNavigate } from 'react-router-dom';
import backgroundImage from './background.jpg';
import googleLogo from './assets/Icons/google-logo.svg';
import './Register.css';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';

const subjectOptions = [
  "math", "physics", "chemistry", "biology", "french", "english", "spanish",
  "history", "philosophy", "coding", "music", "geography"
];

function Register() {
  const {
    statusMessage,
    showResendButton,
    setStatusMessage,
    setShowResendButton,
    resendVerificationEmail
  } = useUser();

  const navigate = useNavigate();
  const statusMsg = useStatusMessage();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [grade, setGrade] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [userType, setUserType] = useState('I am a...');  const [subjects, setSubjects] = useState([]);
  const [step, setStep] = useState(1); // 1 = info, 2 = subject select for tutors
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeCarouselTab, setActiveCarouselTab] = useState(0);

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  // Detect if user is on mobile device
  useEffect(() => {
    const checkIsMobile = () => {
      const width = window.innerWidth;
      const userAgent = navigator.userAgent;
      
      // Check for mobile screen size or mobile user agents
      const isMobileSize = width <= 768;
      const isMobileAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      setIsMobile(isMobileSize || isMobileAgent);
    };

    // Check on mount
    checkIsMobile();

    // Check on resize
    window.addEventListener('resize', checkIsMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const carouselImages = [
    // These will be the placeholder paths - users can add their images here
    '/carousel/Image1.png',
    '/carousel/Image2.png', 
    '/carousel/Image3.png'
  ];

  // Preload all carousel images on component mount
  useEffect(() => {
    carouselImages.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const handleSubjectChange = (subject) => {
    setSubjects((prev) =>
      prev.includes(subject)
        ? prev.filter((s) => s !== subject)
        : [...prev, subject]
    );
  };  const handleResendVerification = async () => {
    try {
      const result = await resendVerificationEmail();
      
      if (result.status === 'sent') {
        setShowResendButton(false);
      } else if (result.status === 'already_verified') {
        setShowResendButton(false);
      }
      // Status message is already set by the resendVerificationEmail function
    } catch (error) {
      console.error('Error resending verification:', error);
      // Error message is already set by the context function
    }
  };
  // Handles the first step (info form) submit
  const handleInfoSubmit = async (e) => {
    e.preventDefault();

    if (userType === 'I am a...') {
      setStatusMessage('Please select your role (Student or Tutor)');
      return;
    }
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim() || !userType.trim()) {
      setStatusMessage('Please fill in all required fields');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      return;
    }
    setPasswordError('');
    setStatusMessage('');

    // Check if user is trying to register as a tutor
    if (userType === "tutor") {
      try {
        const tutorValidationResponse = await api.post('/api/validateTutorEmail', { email: email });
        
        if (!tutorValidationResponse.ok) {
          const errorData = await tutorValidationResponse.json();
          setStatusMessage(errorData.message || 'This email is not authorized to register as a tutor. Please contact the administrator to request access.');
          return;
        }
      } catch (validationError) {
        console.error('Error validating tutor email:', validationError);
        setStatusMessage('Error validating tutor registration. Please try again later.');
        return;
      }
      
      setStep(2); // Go to subject select step
    } else {
      handleFinalSubmit([]); // Student: skip subject select, pass empty array
    }
  };
  // Handles the final submit for both students and tutors (after subject select)
  const handleFinalSubmit = async (finalSubjects) => {
    setIsSubmitting(true);
    try {
      const pendingUsersRef = collection(db, 'pendingUsers');
      const q = query(pendingUsersRef, where('email', '==', email));
      const pendingSnap = await getDocs(q);

      const response = await api.post('/api/checkUserStatus', { email: email });
      const data = await response.json();

      if (!pendingSnap.empty) {
        const deleteOps = pendingSnap.docs.map((docSnap) => deleteDoc(docSnap.ref));
        await Promise.all(deleteOps);
      }

      if (data.userState === 'verified') {
        setStatusMessage('This email is already registered. Please log in.');
        setIsSubmitting(false);
        return;
      }

      // Check if user is trying to register as a tutor
      if (userType === "tutor") {
        try {
          const tutorValidationResponse = await fetch('/api/validateTutorEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email })
          });
          
          if (!tutorValidationResponse.ok) {
            const errorData = await tutorValidationResponse.json();
            setStatusMessage(errorData.message || 'This email is not authorized to register as a tutor. Please contact the administrator to request access.');
            setIsSubmitting(false);
            return;
          }
        } catch (validationError) {
          console.error('Error validating tutor email:', validationError);
          setStatusMessage('Error validating tutor registration. Please try again later.');
          setIsSubmitting(false);
          return;
        }
      }

      if (userType === "tutor" && finalSubjects.length === 0) {
        setStatusMessage("Please select at least one subject you can teach.");
        setIsSubmitting(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;      const actionCodeSettings = {
        url: window.location.origin,
        handleCodeInApp: true,
      };

      await sendEmailVerification(user, actionCodeSettings);

      await setDoc(doc(db, 'pendingUsers', user.uid), {
        firstName,
        lastName,
        email: email.toLowerCase(),
        phone,
        grade,
        userType,
        subjects: finalSubjects,
        createdAt: new Date(),
        emailVerified: false,
        fullData: true      });


      statusMsg.showSuccess('Registration successful! Please check your email for a verification link.');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setGrade('');
      setPassword('');
      setConfirmPassword('');
      setSubjects([]);
      setStep(1);} catch (error) {
      console.error('Error:', error);
      if (error.code === 'auth/email-already-in-use') {
        // Try to sign in the user with the existing account
        try {
          setStatusMessage('Email already exists. Signing you in...');
          const signInResult = await signInWithEmailAndPassword(auth, email, password);
          const existingUser = signInResult.user;

          
          if (!existingUser.emailVerified) {
            setStatusMessage('Account found! Please verify your email to complete registration.');
            setShowResendButton(true);
          } else {
            setStatusMessage('Account found and verified! Please complete your profile if needed.');
            setShowResendButton(false);
          }

          
            // Clear the form since user is now signed in
          setFirstName('');
          setLastName('');
          setEmail('');
          setPhone('');
          setGrade('');
          setPassword('');
          setConfirmPassword('');
          setSubjects([]);
          setStep(1);
          
          // Navigate to Auth page to show verification status
          navigate('/Auth');
          
        } catch (signInError) {
          console.error('Error signing in existing user:', signInError);
          if (signInError.code === 'auth/wrong-password' || signInError.code === 'auth/invalid-credential') {
            setStatusMessage('This email is already registered with a different password. Please use the correct password or reset it.');
          } else {
            setStatusMessage('This email is already registered but could not sign in. Please try logging in instead.');
          }
          setShowResendButton(false);
        }
      } else {
        setStatusMessage('Registration failed. Please try again.');
        setShowResendButton(false);
      }
    }
    setIsSubmitting(false);
  };

  // Animation styles for swipe effect
  const swipeContainerStyle = {
    width: '100%',
    maxWidth: 500,
    margin: '0 auto',
    minHeight: 100,
    transition: 'transform 0.5s cubic-bezier(.77,0,.18,1)',
    position: 'relative',
    background: 'none'
  };

  const swipeHidden = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateX(-100%)',
    transition: 'opacity 0.3s, transform 0.5s cubic-bezier(.77,0,.18,1)'
  };

  const swipeVisible = {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateX(0)',
    transition: 'opacity 0.3s, transform 0.5s cubic-bezier(.77,0,.18,1)'
  };  return (
    <div className="register-page" style={{
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      <div className={`register-main-content ${isMobile ? 'mobile-only-form' : ''}`}>
        {/* Left side - Image carousel - Hidden on mobile */}
        {!isMobile && (
          <div className="register-carousel">
            <div className="carousel-container">
              <div className="carousel-image-container">
                {/* Display all images but only show the active one */}
                {carouselImages.map((src, index) => (
                  <img
                    key={index}
                    src={src}
                    alt={`Carousel ${index + 1}`}
                    className={`carousel-image ${index === activeCarouselTab ? 'active' : ''}`}
                    loading="eager"
                  />
                ))}
              </div>
              <div className="carousel-tabs">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-tab ${index === activeCarouselTab ? 'active' : ''}`}
                    onClick={() => setActiveCarouselTab(index)}
                    aria-label={`View image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Right side - Form */}
        <div className="register-form-section">
          <h1 className="register-title">Create an account</h1>
          <p className="register-subtitle">
            Already have an account? <Link to="/Auth">Login</Link>
          </p>

          <div style={{
            ...swipeContainerStyle,
            marginTop: 0,
            marginBottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch'
          }}>
            {/* Step 1: Info Form */}
            <form
              className="register-form"
              onSubmit={handleInfoSubmit}
              style={{
                ...(step === 1 ? swipeVisible : swipeHidden),
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch'
              }}
            >
              {/* User type selector */}
              <select
                className="user-type-dropdown"
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                required
              >
                <option value="I am a..." disabled>I am a...</option>
                <option value="student">Student</option>
                <option value="tutor">Tutor</option>
              </select>

              {/* Form Grid */}
              <div className="form-grid">
                <input
                  className="register-input"
                  type="text"
                  placeholder="First Name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  className="register-input"
                  type="text"
                  placeholder="Last Name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>

              <div className="form-grid">
                <input
                  className="register-input"
                  type="email"
                  placeholder="Email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  className="register-input"
                  type="tel"
                  placeholder="Phone Number"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="form-grid">
                <input
                  className="register-input"
                  type="password"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <input
                  className="register-input"
                  type="password"
                  placeholder="Confirm Password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>              {userType === 'student' && (
                <div className="grade-field-container">
                  <div className="form-grid full-width">
                    <input
                      className="register-input grade-input"
                      placeholder="Student is in grade..."
                      type="text"
                      required
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {passwordError && (
                <div className="error-message">
                  {passwordError}
                </div>
              )}

              <button
                type="submit"
                className="register-submit-btn"
                disabled={isSubmitting}
              >
                {userType === "tutor" ? "Next" : "Submit"}
              </button>

              {/* Google Sign In Button - styled to match Auth page */}
              <button 
                type="button" 
                className="register-google-btn"
                onClick={() => navigate('/Auth?googleSignIn=true')}
              >
                <img src={googleLogo} alt="Google logo" className="google-logo-icon" />
                Continue with Google
              </button>
            </form>            {/* Step 2: Subject Select for Tutors */}
            {userType === "tutor" && (
              <div
                className="register-form tutor-subjects-form"
                style={{
                  ...(step === 2 ? swipeVisible : swipeHidden),
                  minHeight: 250,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch'
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '1.15rem',
                    color: '#ffffff',
                    marginBottom: '0.5rem',
                    letterSpacing: '0.01em',
                    textAlign: 'center'
                  }}
                >
                  Select the subjects you are comfortable teaching
                </div>                <div className="tutor-subjects-grid">
                  {subjectOptions.map((subject) => (
                    <label
                      key={subject}
                      className={`tutor-subject-label ${subjects.includes(subject) ? 'selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={subjects.includes(subject)}
                        onChange={() => handleSubjectChange(subject)}
                        className="tutor-subject-checkbox"
                      />
                      <span className="tutor-subject-text">
                        {subject.charAt(0).toUpperCase() + subject.slice(1).replace(/([a-z])([A-Z])/g, '$1 $2')}
                      </span>
                    </label>
                  ))}
                </div>
                <div style={{
                  fontSize: '1.05rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginTop: '0.5rem',
                  textAlign: 'center',
                  maxWidth: 440,
                  borderRadius: '6px',
                  padding: '0.7rem 1.2rem',
                  fontWeight: 500,
                  alignSelf: 'center'
                }}>
                  You can update your subjects later in your profile.
                </div>                <div className="tutor-button-container">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="register-back-btn"
                    disabled={isSubmitting}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFinalSubmit(subjects)}
                    className="register-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {statusMessage && (
            <div className="status-message">
              <p>{statusMessage}</p>
              {showResendButton && (
                <button
                  type="button"
                  style={{
                    backgroundColor: '#28a745',
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    marginTop: '1rem'
                  }}
                  onClick={handleResendVerification}
                >
                  Resend Verification Email
                </button>
              )}
            </div>          )}
        </div>
      </div>

      <StatusMessage
        message={statusMsg.message}
        type={statusMsg.type}
        isVisible={statusMsg.isVisible}
        onClose={statusMsg.hideMessage}
        position="top-center"
      />
    </div>
  );
}

export default Register;
